/* eslint-disable react/jsx-no-useless-fragment */
import React, { useMemo, useRef, useState } from 'react'
import { createContext, useContext } from 'use-context-selector'
import Fragment from './Fragment'

interface Props {
    fallback: NonNullable<React.ReactNode> | null
    children: React.ReactNode
    fragmentStyle?: any
    className?: string
}

export const SoftSuspenseContext = createContext<(promise: Promise<any>) => void>(null as any)
export const useSuspenseFn = () => {
    return useContext(SoftSuspenseContext)
}

export const useSuspense = (promise: Promise<any>, deps: any[]) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoPromise = useMemo(() => promise, deps)
    const lastMemoPromise = useRef<Promise<any>>()
    const suspense = useContext(SoftSuspenseContext)

    if (lastMemoPromise.current !== memoPromise) {
        suspense(memoPromise)
        lastMemoPromise.current = memoPromise
    }
}

export const useSuspenseBoolean = (shouldSuspense: boolean) => {
    const suspense = useContext(SoftSuspenseContext)
    const resolveRef = useRef<() => void>()
    const promiseRef = useRef<Promise<any>>()
    const suspended = useRef(false)

    if (suspended.current !== shouldSuspense) {
        if (suspended.current) {
            resolveRef.current()
        } else {
            promiseRef.current = new Promise((resolve) => {
                resolveRef.current = resolve
            })
            suspense(promiseRef.current)
        }
        suspended.current = shouldSuspense
    }
}

const SoftSuspense = (props: Props) => {
    const { fallback, children, fragmentStyle, className } = props

    const [status, setStatus] = useState<'pending' | 'resolved'>('resolved')
    const lastPromise = useRef(Promise.resolve())

    const suspense = useMemo(
        () => (promise: Promise<any>) => {
            if (process.env.NODE_ENV === 'development') {
                // console.info('suspending...')
            }

            setStatus('pending')

            const newPromise = lastPromise.current.then(() => promise)
            lastPromise.current = newPromise
            newPromise.then(() => {
                if (lastPromise.current === newPromise) {
                    if (process.env.NODE_ENV === 'development') {
                        // console.info('suspending resolved')
                    }
                    setStatus('resolved')
                }
            })
        },
        []
    )

    return (
        <>
            <Fragment
                className={className}
                hidden={status !== 'pending'}
                style={status !== 'pending' ? undefined : fragmentStyle}
            >
                {status === 'pending' ? fallback : null}
            </Fragment>

            <Fragment
                className={className}
                hidden={status === 'pending'}
                style={status === 'pending' ? undefined : fragmentStyle}
            >
                <SoftSuspenseContext.Provider value={suspense}>{children}</SoftSuspenseContext.Provider>
            </Fragment>
        </>
    )
}

export default SoftSuspense
